import {
  ChatStatus,
  DASH_CHAT_MODE,
  isChatStatusBusy,
  MAIN_CHAT_MODE,
  type Message,
  ModalityType,
  RESEARCH_CHAT_MODE,
  ULTRA_CHAT_MODE,
  useSDK,
  useStores,
} from '@littlebird/sdk';
import {
  formatGreeting,
  GREETING_LINE_COUNT,
  nextGreetingRotationAt,
  opensTurn,
  resolveGreetingIndex,
} from '@littlebird/ui-utils';
import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { autorun } from 'mobx';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { PAYLOAD_FEATURES, useFeaturePayload } from '@/features/feature-flags';
import { subscriptionStore } from '@/features/subscription/subscription.store';
import { useInactivity } from '@/hooks/useInactivity';
import { useIsInternalUser } from '@/hooks/useIsInternalUser';
import { littlebirdSdk } from '@/lib/sdk-init';
import { getRecommendationsQuery } from '@/query/recommendations';
import { userStore } from '@/stores/user.store';
import { NEW_CHAT_ID } from '@/utils/constants';
import { pathBasename } from '@/utils/files';

import {
  chatStore,
  defaultUserAllowedChatModes,
  NEW_CHAT_MODE_KEY,
} from './chat.store';

export { defaultUserAllowedChatModes };

function storedChatMode(threadId: string | undefined): string | undefined {
  if (!threadId || threadId === NEW_CHAT_ID) {
    return chatStore.chatModeMap[NEW_CHAT_MODE_KEY];
  }
  // The map keeps picks made on this device before modes synced. It gets no new entries
  return (
    littlebirdSdk.stores.threads.get(threadId)?.model ||
    chatStore.chatModeMap[threadId]
  );
}

/**
 * The mode this chat displays and sends with: the stored mode after quota and
 * plan fallbacks.
 */
export function resolveChatMode(threadId: string | undefined): string {
  return resolveEffectiveChatMode(
    storedChatMode(threadId) ??
      (subscriptionStore.isPro ? RESEARCH_CHAT_MODE : MAIN_CHAT_MODE)
  );
}

export const useCurrentChatMode = () => {
  // React Compiler memoizes this hook's return, which skips the MobX read on
  // observer-driven re-renders and leaves the rendered mode stale
  'use no memo';
  const { chatId } = useParams({ strict: false });
  const user = userStore.user;
  const currentChatMode = resolveChatMode(chatId);

  const setCurrentChatMode = useCallback(
    (mode: string, newChatId?: string) => {
      chatStore.setChatMode(mode, chatId, newChatId, user?.allowed_chat_modes);
    },
    [chatId, user?.allowed_chat_modes]
  );

  return {
    currentChatMode,
    setCurrentChatMode,
  };
};

const MODE_LABELS: Record<string, string> = {
  [RESEARCH_CHAT_MODE]: 'Max',
  [DASH_CHAT_MODE]: 'Quick',
  [ULTRA_CHAT_MODE]: 'Ultra',
};

function modeLabel(mode: string): string | null {
  return MODE_LABELS[normalizeMode(mode)] ?? null;
}

/**
 * Ultra shares Max's daily quota, so limit messaging shows a combined label
 * for users who can see Ultra (Pro/internal) and plain "Max" for everyone else.
 */
export function researchQuotaLabel(): string {
  return subscriptionStore.isPro || userStore.isInternalUser
    ? 'Max/Ultra'
    : 'Max';
}

function isResearchMode(mode: string): boolean {
  return (
    mode === RESEARCH_CHAT_MODE ||
    userStore.user?.chat_mode_details?.[mode]?.response_mode === 'research'
  );
}

function normalizeMode(mode: string): string {
  return isResearchMode(mode) ? RESEARCH_CHAT_MODE : mode;
}

function isModeAvailable(mode: string): boolean {
  // Max is the only mode still metered by message count, and Ultra shares its daily quota
  if (mode === ULTRA_CHAT_MODE || isResearchMode(mode))
    return !subscriptionStore.isResearchLimitHit;
  return true;
}

const FALLBACKS: Record<string, string[]> = {
  [ULTRA_CHAT_MODE]: [MAIN_CHAT_MODE, DASH_CHAT_MODE],
  [RESEARCH_CHAT_MODE]: [MAIN_CHAT_MODE, DASH_CHAT_MODE],
  [MAIN_CHAT_MODE]: [DASH_CHAT_MODE, RESEARCH_CHAT_MODE],
  [DASH_CHAT_MODE]: [MAIN_CHAT_MODE, RESEARCH_CHAT_MODE],
};

export function resolveEffectiveChatMode(mode: string): string {
  if (isModeAvailable(mode)) return mode;
  return (
    FALLBACKS[normalizeMode(mode)]?.find(isModeAvailable) ?? MAIN_CHAT_MODE
  );
}

export const useChatModeLimitAutoSwitch = () => {
  const { chatId } = useParams({ strict: false });

  useEffect(() => {
    const dispose = autorun(() => {
      const current = storedChatMode(chatId) ?? MAIN_CHAT_MODE;
      const resolved = resolveEffectiveChatMode(current);
      if (resolved !== current) {
        chatStore.setChatMode(
          resolved,
          chatId,
          undefined,
          userStore.user?.allowed_chat_modes
        );
        if (isModeAvailable(resolved)) {
          const fromSharedQuota =
            current === ULTRA_CHAT_MODE ||
            normalizeMode(current) === RESEARCH_CHAT_MODE;
          const fromLabel = fromSharedQuota
            ? researchQuotaLabel()
            : modeLabel(current);
          const toLabel = modeLabel(resolved);
          const prefix = fromLabel
            ? `${fromLabel} limit reached`
            : 'Message limit reached';
          const suffix = toLabel ? `, switched to ${toLabel}` : '';
          toast.info(`${prefix}${suffix}`, {
            id: 'chat-mode-auto-switch',
          });
        } else {
          toast.info('All daily limits reached. Resets at midnight.', {
            id: 'chat-mode-auto-switch',
          });
        }
      }
    });
    return dispose;
  }, [chatId]);
};

export const useGreeting = () => {
  const firstName = userStore.user?.name?.trim().split(' ')[0] ?? null;
  const canCycle = useIsInternalUser();
  const [index, setIndex] = useState(() => resolveGreetingIndex(new Date()));

  useEffect(() => {
    let timer: number;
    const scheduleNextRotation = () => {
      const now = new Date();
      const ms = Math.max(1000, nextGreetingRotationAt(now) - now.getTime());
      timer = window.setTimeout(() => {
        setIndex(resolveGreetingIndex(new Date()));
        scheduleNextRotation();
      }, ms);
    };
    scheduleNextRotation();
    return () => window.clearTimeout(timer);
  }, []);

  const cycleGreeting = useCallback(() => {
    setIndex((i) => (i + 1) % GREETING_LINE_COUNT);
  }, []);

  return {
    greeting: formatGreeting(index, firstName),
    canCycle,
    cycleGreeting,
    debugLabel: canCycle
      ? `internal: ${index + 1}/${GREETING_LINE_COUNT} — click to cycle`
      : undefined,
  };
};

/** True while the backend is streaming or markdown text is still animating in. */
export const useIsAssistantResponding = (threadId?: string): boolean => {
  const { threads } = useStores();
  if (!threadId) return false;
  const status = threads.getById(threadId)?.status;
  const animatingMessageId = chatStore.getAnimatingMessageId(threadId);
  return status === ChatStatus.Streaming || Boolean(animatingMessageId);
};

/** Basenames the agent committed to surfacing via `run_sandbox` `present` on this message. */
const presentedSandboxBasenames = (message: Message): string[] => {
  const modalities = message.modalitiesUsed;
  if (!modalities) return [];
  const names: string[] = [];
  for (const modality of modalities) {
    if (modality.type === ModalityType.RunSandbox && modality.present) {
      for (const path of modality.present) names.push(pathBasename(path));
    }
  }
  return names;
};

/** A file the agent said it would surface, which the client cannot render yet. */
export type PendingSandboxFile = {
  name: string;
  /** `unavailable` once the retry budget is spent. */
  state: 'preparing' | 'unavailable';
};

const NO_PENDING_NAMES: string[] = [];
const NO_PENDING_FILES: PendingSandboxFile[] = [];

/** How long we keep re-syncing for a presented file before calling it undeliverable. */
const PENDING_FILES_BUDGET_MS = 120_000;
const PENDING_FILES_RETRY_BASE_MS = 1_000;
const PENDING_FILES_RETRY_MAX_MS = 16_000;

/** Bounded re-sync chase for the presented files of one finished turn. */
type PendingFilesChase = {
  startedAt: number;
  attempt: number;
  exhausted: boolean;
};

const nextRetryDelayMs = (attempt: number): number =>
  Math.min(
    PENDING_FILES_RETRY_BASE_MS * 2 ** attempt,
    PENDING_FILES_RETRY_MAX_MS
  );

const useIsDocumentVisible = (): boolean => {
  const [isVisible, setIsVisible] = useState(
    () => document.visibilityState === 'visible'
  );
  useEffect(() => {
    const onChange = () => setIsVisible(document.visibilityState === 'visible');
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  return isVisible;
};

export const usePendingSandboxFiles = (
  threadId: string,
  messageId: string,
  // Only the last assistant message can ever have pending files; callers pass `false` for the
  // rest so we skip the per-message thread scan during streaming (avoids O(messages^2) work on
  // token updates).
  enabled: boolean
): PendingSandboxFile[] => {
  const { threads, messages: messagesStore, files: filesStore } = useStores();
  const sdk = useSDK();
  const isVisible = useIsDocumentVisible();
  const [chase, setChase] = useState<PendingFilesChase | null>(null);
  const status = threads.getById(threadId)?.status;
  const isTurnActive =
    isChatStatusBusy(status) ||
    Boolean(chatStore.getAnimatingMessageId(threadId));
  const wasTurnActiveRef = useRef(isTurnActive);

  const shouldScan =
    enabled && (isTurnActive || chase !== null || wasTurnActiveRef.current);
  const pendingNames = shouldScan
    ? computePendingSandboxFiles(
        messagesStore.getMessagesByThreadId(threadId),
        messageId,
        filesStore
      )
    : NO_PENDING_NAMES;
  const hasPending = pendingNames.length > 0;

  useEffect(() => {
    const turnJustEnded = wasTurnActiveRef.current && !isTurnActive;
    wasTurnActiveRef.current = isTurnActive;
    if (turnJustEnded && hasPending) {
      setChase({ startedAt: Date.now(), attempt: 0, exhausted: false });
      sdk.syncNow().catch(() => {});
      return;
    }
    // Dropping the chase also clears any "undeliverable" verdict it reached.
    if (chase !== null && (!hasPending || isTurnActive)) setChase(null);
  }, [isTurnActive, hasPending, chase, sdk]);

  useEffect(() => {
    if (chase === null || chase.exhausted || !hasPending || !isVisible) return;

    const delay = nextRetryDelayMs(chase.attempt);
    if (Date.now() - chase.startedAt + delay > PENDING_FILES_BUDGET_MS) {
      setChase({ ...chase, exhausted: true });
      return;
    }

    const timer = setTimeout(() => {
      sdk.syncNow().catch(() => {});
      setChase((current) =>
        current === null ? null : { ...current, attempt: current.attempt + 1 }
      );
    }, delay);
    return () => clearTimeout(timer);
  }, [chase, hasPending, isVisible, sdk]);

  if (!hasPending) return NO_PENDING_FILES;
  const state = chase?.exhausted ? 'unavailable' : 'preparing';
  return pendingNames.map((name) => ({ name, state }));
};

// Matches presented sandbox basenames against resolved file metadata so non-sandbox attachments
// (e.g. generated images) in the same turn don't wrongly count as "arrived" and hide the
// placeholders before the sandbox file's metadata syncs.
const computePendingSandboxFiles = (
  messages: Message[],
  messageId: string,
  filesStore: { getById(id: string): { filename: string } | undefined }
): string[] => {
  if (messages.length === 0) return NO_PENDING_NAMES;

  let turnStartIdx = -1;
  let lastAssistantIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (lastAssistantIdx === -1 && messages[i].role === 'assistant') {
      lastAssistantIdx = i;
    }
    if (opensTurn(messages[i])) {
      turnStartIdx = i;
      break;
    }
  }
  if (lastAssistantIdx === -1 || messages[lastAssistantIdx].id !== messageId) {
    return NO_PENDING_NAMES;
  }

  const presented = new Set<string>();
  const arrivedBasenames = new Set<string>();
  for (let i = turnStartIdx + 1; i < messages.length; i++) {
    const message = messages[i];
    if (message.role !== 'assistant') continue;
    for (const name of presentedSandboxBasenames(message)) presented.add(name);
    for (const fileId of message.fileIds ?? []) {
      const file = filesStore.getById(fileId);
      if (file) arrivedBasenames.add(pathBasename(file.filename));
    }
  }
  const pending = [...presented].filter((name) => !arrivedBasenames.has(name));
  return pending.length > 0 ? pending : NO_PENDING_NAMES;
};

type FirstChatRecommendationsPayload = {
  questions?: string[];
};

const FIRST_CHAT_QUESTIONS_REFRESH_MS = 2 * 60 * 1000;

const SUGGESTION_CHIPS_MAX_ACCOUNT_AGE_DAYS = 14;

export const areSuggestionChipsVisible = (): boolean => {
  const accountAgeDays = userStore.accountAgeDays;
  if (accountAgeDays === null) return false;
  return accountAgeDays < SUGGESTION_CHIPS_MAX_ACCOUNT_AGE_DAYS;
};

export const useNewChatSuggestions = (): string[] => {
  const { data } = useQuery(getRecommendationsQuery());
  const { threads } = useStores();
  const payload = useFeaturePayload<FirstChatRecommendationsPayload>(
    PAYLOAD_FEATURES.FIRST_CHAT_RECOMMENDATIONS
  );

  const backendQuestions = data?.recommended_questions ?? [];
  const [shownBackendQuestions, setShownBackendQuestions] =
    useState(backendQuestions);

  useInactivity({
    duration: FIRST_CHAT_QUESTIONS_REFRESH_MS,
    onInactive() {
      setShownBackendQuestions(backendQuestions);
    },
  });

  const hasPriorChat = threads.visibleChats.some(
    (chat) => chat.id !== NEW_CHAT_ID
  );
  const firstChatQuestions = payload?.questions;
  const showFirstChatQuestions =
    threads.isLoaded && !hasPriorChat && Boolean(firstChatQuestions?.length);

  return showFirstChatQuestions && firstChatQuestions
    ? firstChatQuestions
    : shownBackendQuestions;
};
